import React from 'react';
import Grid from '@material-ui/core/Grid';
import H1 from 'apps/Pdf/components/common/H1';
import PageBreak from 'apps/Pdf/components/common/PageBreak';
import useStyles from './GlossaryStyle';

interface Props {
  keys: string[];
  noBreak?: boolean;
  useInflation?: boolean;
}

const Glossary = (props: Props) => {
  const classes = useStyles();
  const isInflation = typeof props.useInflation === 'undefined' ? true : props.useInflation;

  const glossary: { [key: string]: { term: string; description: string } } = {
    plannedDeposits: {
      term: 'Plánované vklady',
      description:
        'Edward s plánovanými vklady počítá při každoměsíčním rozdělování majetku mezi jednotlivá portfolia. Je tedy v zájmu zákazníka hlásit změny v pravidelných či dalších plánovaných vkladech v předstihu.',
    },
    requiredReserves: {
      term: 'Nastavené rezervy',
      description:
        'Uvedená částka bude držena jako rezerva ve vybraném portfoliu (krátkodobém/střednědobém) a po dobu jejího trvání nebude použita na zákazníkovy cíle. Prostředky budou investovány dle investiční strategie nastavené v rámci daného portfolia (krátkodobého/střednědobého). Po ukončení rezervy se peníze automaticky přesunou do dlouhodobého portfolia, kde budou investovány dle investiční strategie nastavené v dlouhodobém portfoliu. Pokud je u rezervy nastaveno "zrušit při xx% poklesu", pak může dojít k ukončení rezervy při nastaveném poklesu akciových trhů. Při tom se měří hodnota globálního akciového indexu MSCI ACWI a pokles se měří jako procentuální pokles od nejvyšší naměřené hodnoty indexu v průběhu trvání rezervy.',
    },
    expectedValueOfProperty: {
      term: 'Očekávaný vývoj hodnoty majetku',
      description: `Očekávaná hodnota majetku je kalkulována na základě očekávaných vkladů a výběrů a očekávaného průměrného zhodnocení jednotlivých portfolií.
        Hodnota je vyjádřena v ${
          isInflation
            ? 'současných hodnotách (snížena o předpokládanou inflaci).'
            : 'budoucích hodnotách (bez zohlednění inflace).'
        }`,
    },
    requiredRent: {
      term: 'Potřebná renta',
      description:
        'Pevně stanovená částka vypočítaná na základě předpokládaných příjmů a výdajů klienta v důchodu. V kalkulaci počítáme s navyšováním této částky o uvedenou inflaci.',
    },
    manualRent: {
      term: 'Požadovaná renta',
      description:
        'Pevně stanovená částka, určená jako požadavek klienta. V kalkulaci počítáme s navyšováním této částky o uvedenou inflaci.',
    },
    calculatedRent: {
      term: 'Dopočítaná renta',
      description:
        'Vypočítaná částka, na základě předpokládaného vývoje investičního plánu. Tato částka se bude průběžně přepočítávat a může se tedy v čase měnit. V kalkulaci počítáme s navyšováním této částky o uvedenou inflaci.',
    },
    monthlyRentAmount: {
      term: 'Výše měsíční renty',
      description:
        'Očekávaná výše vyplácené pravidelné měsíční renty. Částka je vyjádřena v současných hodnotách (sníženo o předpokládanou inflaci).',
    },
    portfolio: {
      term: 'Portfolio',
      description:
        'Je souhrn cenných papírů, hotovosti a dalších finančních nástrojů, evidovaný a vykazovaný odděleně na majetkovém účtu zákazníka.',
    },
    strategy: {
      term: 'Strategie',
      description:
        'Je modelové složení portfolia, které odpovídá zákazníkovu investičnímu profilu a podle kterého budou do portfolia nakupovány a prodávány investiční nástroje.',
    },
    strategyRiskiness: {
      term: 'Rizikovost strategie',
      description:
        'Je očekávaná míra kolísání zvolené investiční strategie na stupnici od 1 (žádné nebo mírné kolísání) do 10 (výrazné kolísání).',
    },
    expectedReturn: {
      term: '* Kalkulovaný výnos',
      description:
        'Kalkulovaný výnos portfolia je stanoven jako aritmetický průměr očekávaných výnosů jednotlivých investičních kategorií zastoupených v daném portfoliu, ponížený o předpokládané náklady na investování a zaokrouhlený na půl procenta. Při stanovení očekávaných výnosů investičních kategorií vycházíme z dlouhodobých průměrných výnosů srovnávacích indexů, které danou kategorii reprezentují, a přihlížíme k aktuálním úrokovým sazbám, výnosům do splatnosti dluhopisů, inflaci a valuacím akcií. Je potřeba brát na zřetel, že uváděné Kalkulované výnosy vychází z výnosů minulých a nejsou zárukou skutečně dosažených výnosů v budoucnu. Skutečné výnosy portfolia v budoucnu se mohou od kalkulovaných výrazně lišit, a mohou dosáhnout i záporných hodnot.',
    },
    acceptedLoss: {
      term: 'Akceptovatelný pokles',
      description:
        'Vyjadřuje orientační hranici, o kolik procent může hodnota portfolia poklesnout. Tato hranice však není zárukou, že nedojde k poklesu vyššímu.',
    },
    currencyHedging: {
      term: 'Měnové zajištění',
      description:
        'Pokud je zapnuté, budou zákazníkovi do portfolia zařazeny investiční nástroje (měnové forwardy a swapy), jejichž cílem je snížit riziko měnových výkyvů v portfoliu.',
    },
    automaticRebalancing: {
      term: 'Automatické rebalancování',
      description:
        'Pokud je zapnuté, bude zákazníkovo portfolio automaticky rebalancováno, pokud dojde v důsledku rozdílného vývoje tržních cen k odklonu aktuálních vah jednotlivých instrumentů od nastavené investiční strategie.',
    },
    isin: { term: 'ISIN', description: 'Je mezinárodní kód investičního nástroje.' },
    investmentCategory: {
      term: 'Investiční kategorie',
      description:
        'Je obecné označení, které vyjadřuje, jaké zaměření mají podkladová aktiva vybraných investičních fondů.',
    },
    selectedFond: {
      term: 'Vybraný fond',
      description: 'Je konkrétní investiční nástroj, který zákazník drží.',
    },
    annualCost: {
      term: 'Roční nákladovost',
      description:
        'Je údaj o nákladovosti uvedeného fondu. Tato nákladovost se promítá do tržního kurzu vybraného fondu, není účtována zákazníkovi zvlášť.',
    },
    morningstarRating: {
      term: 'Morningstar Rating',
      description:
        'Je hodnocení, které udělují vybraným fondům analytici ze společnosti Morningstar, největší světové nezávislé analytické agentury. Některé fondy nejsou analytiky hodnoceny.',
    },
    targetShare: {
      term: 'Cílové zastoupení',
      description:
        'Udává, kolik procent z celkové částky v portfoliu by mělo být zainvestováno ve vybrané investiční kategorii/fondu.',
    },
    totalValueOfPropertyInPortfolios_CZK: {
      term: 'Celková hodnota majetku v portfoliích',
      description:
        'Udává celkovou hodnotu majetku na účtu. Spočítá se jako součet aktuálních hodnot majetku ve všech portfoliích přepočítaných do CZK, včetně držené hotovosti.',
    },
    totalValueOfPropertyInPortfolios_EUR: {
      term: 'Celková hodnota majetku v portfoliích',
      description:
        'Udává celkovou hodnotu majetku na účtu. Spočítá se jako součet aktuálních hodnot majetku ve všech portfoliích přepočítaných do EUR, včetně držené hotovosti.',
    },
    deposits: {
      term: 'Vklady',
      description:
        'Je součet částek, které zákazník v daném období vložil v hotovosti na svůj investiční účet.',
    },
    withdrawals: {
      term: 'Výběry',
      description:
        'Je součet částek, které zákazník v daném období vybral v hotovosti na svůj bankovní účet.',
    },
    portfolioYield: {
      term: 'Výnosy portfolií',
      description:
        'Je součet hrubých výnosů z držených cenných papírů (růst/pokles jejich tržní ceny, připsané dividendy) za reportované období.',
    },
    costs: {
      term: 'Náklady (poplatky)',
      description: 'Je součet všech poplatků naúčtovaných zákazníkovi během daného období.',
    },
    instrument: { term: 'Instrument', description: 'Název cenného papíru' },
    date: { term: 'Datum', description: 'Datum, kdy byla transakce provedena/zadána (trade date)' },
    type: {
      term: 'Typ',
      description: 'Informace o povaze provedené transakce z hlediska nákup/prodej',
    },
    pieces: { term: 'Počet kusů', description: 'Počet kusů cenných papírů, včetně frakcí' },
    currentRate: { term: 'Aktuální kurz', description: 'Aktuální kurz dané měny' },
    forwardRate: {
      term: 'Forwardový kurz',
      description:
        'Pevně stanovený (zasmluvněný) kurz měny k uvedenému datu splatnosti daného termínovaného obchodu (forwardu/swapu)',
    },
    unrealizedProfitLoss: {
      term: 'Nerealizovaný zisk/ztráta',
      description:
        'Aktuální nerealizovaný zisk nebo ztráta z termínovaného měnového obchodu (forwardu/swapu), způsobený/á pohybem měnového kurzu.',
    },
    atmInstruction: {
      term: 'Pokyn ATM',
      description:
        'Instrukce obchodníkovi, koupit cenný papír způsobem „At The Market“ – nakoupit, či prodat požadované množství cenných papírů za kurz, který je nejvýhodnější v době jeho doručení na trh.',
    },
    entryFee: {
      term: 'Jednorázový vstupní poplatek',
      description:
        'Uvedená částka je splatná bezprostředně po uzavření Smlouvy a je stržena z prvních plateb zaslaných zákazníkem na jeho peněžní účet.',
    },
    prepaidValue: {
      term: 'Předplacená částka k investování CELKEM',
      description:
        'Je objem zákazníkem zaslaných plateb, které budou investovány, aniž by byly zatíženy Následným vstupním poplatkem.',
    },
    contFees: {
      term: 'Následné vstupní poplatky',
      description:
        'Je sazba vstupního poplatku, který se použije v okamžiku, kdy objem zaslaných plateb překročí celkovou Předplacenou částku k investování. Všechny zaslané platby (pravidelné i jednorázové) nad rámec Předplacené částky k investování budou zatíženy uvedeným Následným vstupním poplatkem.',
    },
    cappedFee: {
      term: 'Strop vstupního poplatku',
      description:
        'Je maximální kumulovaná částka stržených vstupních poplatků (Jednorázových i následných). Jakmile suma stržených vstupních poplatků dosáhne uvedeného stropu, následné vstupní poplatky z dalších přijatých plateb se dále neúčtují.',
    },

    serviceFee: {
      term: 'Poplatek za využívání služby (standardní a snížený)',
      description:
        'Účtuje se měsíčně, jako součet násobků průměrné tržní hodnoty majetku v jednotlivých zákazníckých Portfoliích za předchozí kalendářní měsíc a 1/12 příslušné roční sazby podle Sazebníku (dle rizikovosti a objemu). Průměrná tržní hodnota majetku v Portfoliu v předchozím kalendářním měsíci se spočítá jako součet tržních hodnot majetku v každém dni, vydělený počtem dnů v měsíci.',
    },
    avgPortfolioValue: {
      term: 'Průměrná hodnota majetku v Portfoliích',
      description:
        'Počítá se na denní bázi jako průměrná tržní hodnota majetku v Portfoliích v daném kalendářním měsíci.',
    },
    performanceFee: {
      term: 'Poplatek za využívání služby - výkonnostní',
      description:
        'Počítá se jako uvedená procentní sazba z čistého zisku z veškerého majetku zákazníka na daném zákazníckém účtu ve službě Edward. Účtuje se měsíčně metodou High Wattermark - každý poslední den v měsíci se sleduje nejvyšší dosažený čistý zisk na účtu za celou jeho historii a poplatek se účtuje z rozdílu hodnoty aktuálně dosaženého čistého zisku k poslednímu dni v předchozím měsíci a nejvyšší dosažené hodnoty čistého zisku k posledním dnům všech dřívějších měsíců, je-li kladná. Čistý zisk se spočítá jako celková aktuální hodnota majetku na účtu mínus celková hodnota vložených prostředků plus celková hodnota vybraných prostředků.',
    },
    extraFees: {
      term: 'Nadstandardní služby',
      description:
        'Nadstandardní službou je myšlen zejména úkon na vyžádání zákazníka, např. šetření plateb, vyhledávání dokladů a nahrávek komunikace, pořizování kopií dokladů a dokumentace, vydávání nestandardních potvrzení pro třetí strany, převody cenných papírů apod.',
    },
    totalProfit_CZK: {
      term: 'Aktuální zisk/ztráta',
      description:
        'Hodnota v Kč zobrazuje peněžní zisk nebo ztrátu z vašich investic na účtu v Edwardovi. Je to rozdíl mezi aktuální hodnotou všech vašich portfolií a hodnotou Vašich čistých vkladů. Hodnota v procentech je průměrný roční výnos, který je počítán metodou peněžně váženého výnosu. Ten je ovlivněn nejen výnosem cenných papírů, které držíte, ale i velikostí a načasováním vašich vkladů a výběrů v průběhu trvání vaší investice. Prakticky ukazuje individuální výkonnost Vás, jako investora.',
    },
    totalProfit_EUR: {
      term: 'Aktuální zisk/ztráta',
      description:
        'Hodnota v EUR zobrazuje peněžní zisk nebo ztrátu z vašich investic na účtu v Edwardovi. Je to rozdíl mezi aktuální hodnotou všech vašich portfolií a hodnotou Vašich čistých vkladů. Hodnota v procentech je průměrný roční výnos, který je počítán metodou peněžně váženého výnosu. Ten je ovlivněn nejen výnosem cenných papírů, které držíte, ale i velikostí a načasováním vašich vkladů a výběrů v průběhu trvání vaší investice. Prakticky ukazuje individuální výkonnost Vás, jako investora.',
    },
    profit_CZK: {
      term: 'Výnos',
      description:
        'Hodnota v Kč zobrazuje peněžní zisk nebo ztrátu z vašich investic do vybraného portfolia. Je to rozdíl mezi aktuální hodnotou všech aktiv v portfoliu a hodnotou Vašich čistých vkladů. Hodnota v procentech je průměrný roční výnos vybraného portfolia, který je počítán metodou peněžně váženého výnosu. Ten je ovlivněn nejen výnosem cenných papírů, které držíte, ale i velikostí a načasováním vašich vkladů a výběrů v průběhu trvání vaší investice. Prakticky ukazuje individuální výkonnost Vás, jako investora.',
    },
    profit_EUR: {
      term: 'Výnos',
      description:
        'Hodnota v EUR zobrazuje peněžní zisk nebo ztrátu z vašich investic do vybraného portfolia. Je to rozdíl mezi aktuální hodnotou všech aktiv v portfoliu a hodnotou Vašich čistých vkladů. Hodnota v procentech je průměrný roční výnos vybraného portfolia, který je počítán metodou peněžně váženého výnosu. Ten je ovlivněn nejen výnosem cenných papírů, které držíte, ale i velikostí a načasováním vašich vkladů a výběrů v průběhu trvání vaší investice. Prakticky ukazuje individuální výkonnost Vás, jako investora.',
    },
    interests: {
      term: 'Připsané úroky',
      description:
        'Jsou úroky připsané na Váš účet z peněz, které jsou v portfoliu drženy jako úročená hotovost. Procentní výše úroku se průběžně mění dle aktuálních podmínek protistran (bank) a není dopředu známá. Připsaná částka úroků je nezdaněná a klient by jí měl uvést ve svém daňovém přiznání.',
    },
    esg: {
      term: 'ESG investování',
      description:
      'ESG investování je forma zodpovědného investování, která bere v úvahu enviromentální (životní prostředí), sociální (společenská odpovědnost) a governance (způsob řízení firmy) faktory, aby posoudila firmy a státy nejen z pohledu finanční návratnosti investice, ale i z pohledu jejich dopadů na životní prostředí a udržitelnost.'
    },
    portfolioManager: {
      term: 'Portfolio manager',
      description:
      'Je WOOD Retail Investments a.s.'
    },
    individualPortfolio: {
      term: 'Individuální portfolio',
      description: 'Portfolio, které je sestaveno individuálně dle výběru požadovaných investičních kategorií klientem. V rámci každé kategorie portfoliomanažer vybírá odpovídající investiční nástroj.'
    }

  };

  return (
    <>
      {!props.noBreak && <PageBreak />}
      <H1>Vysvětlení vybraných pojmů</H1>
      {props.keys.map((key: string) => {
        return key !== '' && glossary[key] ? (
          <Grid container className={classes.entry} key={key}>
            <Grid item xs={3}>
              {glossary[key].term}
            </Grid>
            <Grid item xs={9}>
              {glossary[key].description}
            </Grid>
          </Grid>
        ) : null;
      })}
    </>
  );
};

export default Glossary;
